import { useState, useEffect, useMemo } from "react";
import PageLayout from "../../../Layouts/PageLayout/PageLayout";
import TabsNavigation from "../../../components/common/TabsNavigation/TabsNavigation";
import Button from "../../../components/common/Button/Button";
import InputDropdown from "../../../components/inputs/InputDropdown/InputDropdown";
import BarChartGraph from "../../../components/common/Charts/BarChartGraph";
import Table from "../../../components/common/Table/Table";
import { FiDownload } from "react-icons/fi";
import { getApiInstance } from "../../../utils/api";
import "../cantidadesGeneradas/CantidadesGeneradas.scss";
import "./Costes.scss";

const columnasCostes = [
    { key: "cadena", label: "Cadena", visible: true, sortable: true },
    { key: "tipoObra", label: "Tipo de Obra", visible: true, sortable: true },
    { key: "pais", label: "País", visible: true, sortable: true },
    {
        key: "fechaTratamiento",
        label: "Fecha Tratamiento",
        visible: true,
        sortable: true,
        render: (row) =>
            new Date(row.fechaTratamiento).toLocaleDateString("es-ES"),
    },
    {
        key: "costePrevisto",
        label: "Coste Previsto",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) =>
            new Intl.NumberFormat("es-ES", {
                style: "currency",
                currency: "EUR",
            }).format(row.costePrevisto),
    },
    {
        key: "costeReal",
        label: "Coste Real",
        visible: true,
        sortable: true,
        type: "number",
        render: (row) =>
            new Intl.NumberFormat("es-ES", {
                style: "currency",
                currency: "EUR",
            }).format(row.costeReal),
    },
];

export default function Costes() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState("grafica");

    const [informeCostes, setInformeCostes] = useState([]);

    // Estados para los filtros
    const [selectedCadena, setSelectedCadena] = useState("all");
    const [selectedTipoObra, setSelectedTipoObra] = useState("all");
    const [selectedPais, setSelectedPais] = useState("all");
    const [selectedAnio, setSelectedAnio] = useState("all");

    useEffect(() => {
        const fetchInformeCostes = async () => {
            setLoading(true);
            setError(null);
            try {
                const api = getApiInstance();
                const response = await api.get(`/api/Informe/GetCostes`);
                setInformeCostes(response.data);
            } catch (err) {
                setError(
                    "No se ha podido cargar el informe. Vuelve a intentarlo más tarde."
                );
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchInformeCostes();
    }, []);

    const filterOptions = useMemo(() => {
        if (informeCostes.length === 0) {
            return { cadenas: [], tiposObra: [], paises: [], anios: [] };
        }
        const cadenasUnicas = [
            ...new Set(informeCostes.map((item) => item.cadena)),
        ].filter(Boolean);
        const tiposObraUnicos = [
            ...new Set(informeCostes.map((item) => item.tipoObra)),
        ].filter(Boolean);
        const paisesUnicos = [
            ...new Set(informeCostes.map((item) => item.pais)),
        ].filter(Boolean);
        const aniosUnicos = [
            ...new Set(
                informeCostes.map((item) =>
                    new Date(item.fechaTratamiento).getFullYear()
                )
            ),
        ];
        return {
            cadenas: [
                { value: "all", label: "Todas las cadenas" },
                ...cadenasUnicas.map((c) => ({ value: c, label: c })),
            ],
            tiposObra: [
                { value: "all", label: "Todos los tipos de obra" },
                ...tiposObraUnicos.map((t) => ({ value: t, label: t })),
            ],
            paises: [
                { value: "all", label: "Todos los países" },
                ...paisesUnicos.map((p) => ({ value: p, label: p })),
            ],
            anios: [
                { value: "all", label: "Todos los años" },
                ...aniosUnicos
                    .sort((a, b) => b - a)
                    .map((a) => ({ value: a, label: a.toString() })),
            ],
        };
    }, [informeCostes]);

    const datosFiltrados = useMemo(() => {
        return informeCostes.filter((item) => {
            const anioTratamiento = new Date(
                item.fechaTratamiento
            ).getFullYear();
            return (
                (selectedCadena === "all" || item.cadena === selectedCadena) &&
                (selectedTipoObra === "all" ||
                    item.tipoObra === selectedTipoObra) &&
                (selectedPais === "all" || item.pais === selectedPais) &&
                (selectedAnio === "all" || anioTratamiento === selectedAnio)
            );
        });
    }, [
        informeCostes,
        selectedCadena,
        selectedTipoObra,
        selectedPais,
        selectedAnio,
    ]);

    const { datosBarChart, totalCosteReal, totalCostePrevisto } =
        useMemo(() => {
            const agregados = datosFiltrados.reduce((acc, item) => {
                if (!acc[item.cadena]) {
                    acc[item.cadena] = { costeReal: 0, costePrevisto: 0 };
                }
                acc[item.cadena].costeReal += item.costeReal;
                acc[item.cadena].costePrevisto += item.costePrevisto;
                return acc;
            }, {});

            const datosProcesados = Object.keys(agregados).map((cadena) => ({
                name: cadena,
                costeReal: agregados[cadena].costeReal,
                costePrevisto: agregados[cadena].costePrevisto,
            }));

            const totalReal = datosFiltrados.reduce(
                (sum, item) => sum + item.costeReal,
                0
            );
            const totalPrevisto = datosFiltrados.reduce(
                (sum, item) => sum + item.costePrevisto,
                0
            );

            return {
                datosBarChart: datosProcesados,
                totalCosteReal: totalReal,
                totalCostePrevisto: totalPrevisto,
            };
        }, [datosFiltrados]);

    const formatCurrency = (value) =>
        new Intl.NumberFormat("es-ES", {
            style: "currency",
            currency: "EUR",
        }).format(value);

    const tabs = [
        { id: "grafica", label: "Gráfica" },
        { id: "listado", label: "Listado" },
    ];

    const renderFilters = () => (
        <div className="filters-section-common">
            <div className="filter-group">
                <InputDropdown
                    options={filterOptions.cadenas}
                    value={filterOptions.cadenas.find(
                        (c) => c.value === selectedCadena
                    )}
                    onChange={(option) => setSelectedCadena(option.value)}
                    placeholder="Seleccionar cadena"
                    labelVisible={false}
                />
                <InputDropdown
                    options={filterOptions.tiposObra}
                    value={filterOptions.tiposObra.find(
                        (t) => t.value === selectedTipoObra
                    )}
                    onChange={(option) => setSelectedTipoObra(option.value)}
                    placeholder="Seleccionar tipo de obra"
                    labelVisible={false}
                />
                <InputDropdown
                    options={filterOptions.paises}
                    value={filterOptions.paises.find(
                        (p) => p.value === selectedPais
                    )}
                    onChange={(option) => setSelectedPais(option.value)}
                    placeholder="Seleccionar país"
                    labelVisible={false}
                />
                <InputDropdown
                    options={filterOptions.anios}
                    value={filterOptions.anios.find(
                        (a) => a.value === selectedAnio
                    )}
                    onChange={(option) => setSelectedAnio(option.value)}
                    placeholder="Seleccionar año"
                    labelVisible={false}
                />
            </div>
        </div>
    );

    const renderGraficaContent = () => (
        <div className="tab-content-with-filters">
            {renderFilters()}

            <div className="cantidades-generadas-content">
                <div className="graficas-card">
                    <div className="charts-section">
                        <div className="chart-container bar-chart-container">
                            <div className="chart-header">
                                <h3>Costes</h3>
                                <span className="total-amount">
                                    <strong>Real:</strong>{" "}
                                    {formatCurrency(totalCosteReal)}
                                </span>
                                <span className="total-amount">
                                    <strong>Previsto:</strong>{" "}
                                    {formatCurrency(totalCostePrevisto)}
                                </span>
                            </div>
                            <div className="chart-wrapper">
                                <BarChartGraph
                                    data={{ all: datosBarChart }}
                                    activeTab="all"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderListadoContent = () => (
        <div className="tab-content-with-filters">
            {renderFilters()}

            <div className="listado-content">
                <Table
                    columns={columnasCostes}
                    dataTable={datosFiltrados}
                    keyProp="codObra"
                    sortProp="fechaTratamiento"
                    showCheckboxes={false}
                    noResultsMessage="No se han encontrado costes con los filtros aplicados."
                    elementName="coste"
                    loading={loading}
                    rowsPerPage={10}
                />
            </div>
        </div>
    );

    return (
        <div className="costes-page">
            <PageLayout
                title="Informe de Costes"
                subtitle="Análisis detallado de costes reales vs. previstos por obra"
                actions={[
                    <Button
                        key="export"
                        componentType="button"
                        variant="outlined"
                        startIcon={<FiDownload />}
                    >
                        Exportar a excel
                    </Button>,
                ]}
            >
                <TabsNavigation
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                {error && <div className="error-message">{error}</div>}

                {loading ? (
                    <div className="loading-message">Cargando datos...</div>
                ) : activeTab === "grafica" ? (
                    renderGraficaContent()
                ) : (
                    renderListadoContent()
                )}
            </PageLayout>
        </div>
    );
}
